import { useMutation, useQuery, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPost,
  reactQueryPut,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { CUSTOM_RESOURCES_BASE_QUERY_KEY } from "@circle-react/pages/BotBuilder/KnowledgePage/queries";
import { useToast } from "@circle-react-shared/uikit/ToastV2";

const INDEX_QUERY_KEY = ["internal_api", "community_bot", "snippets"];

interface BulkDeleteSnippetRequest {
  ids: number[];
}

interface UseModalProps {
  onSuccess: () => void;
}

interface EditSnippetRequest {
  id: number;
  title: string;
}

interface ApiEditSnippetRequest {
  title: string;
}

interface ApiEditSnippetResponse {
  id: number;
  title: string;
}

interface Snippet {
  body: string;
  title: string;
}

interface SnippetItem {
  body: string;
  id: number;
  title: string;
}

export interface CommunityBotProfilePreview {
  avatar_url: string;
  id: number;
  name: string;
}

export interface CommunityMemberPreview {
  avatar_url?: string;
  id: number;
  name: string;
}

export interface SnippetPreview {
  body: object;
  community_bot_profiles: CommunityBotProfilePreview[];
  created_at: string;
  creator?: CommunityMemberPreview;
  file_size: string;
  file_url: string;
  id: number;
  title: string;
  truncated: boolean;
  type: string;
  updated_at: string;
  updater?: CommunityMemberPreview;
  used_in_replies: number;
}

interface SnippetProp {
  body: object;
  id: number;
  name: string;
}

interface SnippetResponse {
  count: number;
  records: Array<SnippetProp>;
}

export const useIndexSnippets = ({
  currentPage,
  title,
  type,
}: {
  currentPage: number;
  title?: string;
  type?: string;
}) =>
  useQuery<SnippetResponse>({
    queryKey: [...INDEX_QUERY_KEY, currentPage, title, type],
    queryFn: () =>
      reactQueryGet(
        internalApi.communityBot.snippets.index({
          params: {
            page: currentPage,
            per_page: 30,
            ...(title ? { title } : {}),
            ...(type ? { type } : {}),
          },
        }),
      ),
  });

export const useShowSnippet = ({ id }: { id: number }) =>
  useQuery<SnippetItem, Error>(["snippets", id], () =>
    reactQueryGet(internalApi.communityBot.snippets.show({ id })),
  );

export const useShowPreviewSnippet = ({ id }: { id: number }) =>
  useQuery<SnippetPreview, Error>(["snippets", id, "preview"], () =>
    reactQueryGet(internalApi.communityBot.snippets.showPreview({ id })),
  );

export const useBulkDeleteSnippets = ({ onSuccess }: UseModalProps) => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation<void, Error, BulkDeleteSnippetRequest>(
    (data: BulkDeleteSnippetRequest) =>
      reactQueryDelete(internalApi.communityBot.snippets.bulkDestroy(), {
        ids: data.ids,
      }),
    {
      onSuccess: (_, variables) => {
        void queryClient.invalidateQueries({ queryKey: INDEX_QUERY_KEY });
        void queryClient.invalidateQueries({
          queryKey: CUSTOM_RESOURCES_BASE_QUERY_KEY,
        });
        variables.ids?.forEach(id => {
          void queryClient.invalidateQueries(["snippet", id]);
        });
        onSuccess();
        success(
          t(
            "settings.community_bot.knowledge.custom.resources_deleted_success",
            {
              count: variables.ids?.length,
            },
          ),
        );
      },
      onError: () => {
        error(t("request_failure_message"));
      },
    },
  );
};

export const useEditSnippet = ({ onSuccess }: UseModalProps) => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation<ApiEditSnippetResponse, Error, EditSnippetRequest>(
    (data: EditSnippetRequest) => {
      const { id, ...rest } = data;
      const apiData: ApiEditSnippetRequest = {
        ...rest,
      };
      return reactQueryPut(
        internalApi.communityBot.snippets.update({ id }),
        apiData,
      );
    },
    {
      onSuccess: data => {
        void queryClient.invalidateQueries({ queryKey: INDEX_QUERY_KEY });
        void queryClient.invalidateQueries({
          queryKey: CUSTOM_RESOURCES_BASE_QUERY_KEY,
        });
        void queryClient.invalidateQueries(["snippets", data.id]);
        onSuccess();
        success(t("changes_saved"));
      },
      onError: () => {
        error(t("request_failure_message"));
      },
    },
  );
};

export const useCreateSnippet = ({ onSuccess }: { onSuccess: () => void }) => {
  const { success, error } = useToast();
  const queryClient = useQueryClient();

  return useMutation(
    (formData: Snippet) =>
      reactQueryPost(internalApi.communityBot.snippets.create(), {
        snippet: formData,
      }),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: INDEX_QUERY_KEY });
        void queryClient.invalidateQueries({
          queryKey: CUSTOM_RESOURCES_BASE_QUERY_KEY,
        });
        success(
          t("settings.community_bot.knowledge.custom.resource_created_success"),
        );
        onSuccess();
      },
      onError: () => error(t("quick_post.toasts.failed_to_create")),
    },
  );
};

export const useCreateFromFilesSnippet = ({
  onSuccess,
}: {
  onSuccess: () => void;
}) => {
  const { success, error } = useToast();
  const queryClient = useQueryClient();

  return useMutation(
    (files: string[]) =>
      reactQueryPost(internalApi.communityBot.snippets.createFromFiles(), {
        files,
      }),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: INDEX_QUERY_KEY });
        void queryClient.invalidateQueries({
          queryKey: CUSTOM_RESOURCES_BASE_QUERY_KEY,
        });
        success(t("settings.community_bot.knowledge.custom.files_uploaded"));
        onSuccess();
      },
      onError: () => error(t("quick_post.toasts.failed_to_create")),
    },
  );
};
